import { Controller } from "@hotwired/stimulus";

// The Display section renders a per-brand override block for every brand but shows only the ones that
// already have an override; the rest are added on demand from the picker. Adding a brand reveals its
// (already server-rendered) block and hides its picker chip; removing clears the block's fields (so the
// save deletes the override) and returns the brand to the picker. Every brand has both a row and a
// picker chip; exactly one is visible at a time.
export default class extends Controller {
  static targets = ["row", "option", "picker"];

  add(event) {
    event.preventDefault();
    const brandId = event.params.brand;
    const row = this.rowFor(brandId);
    if (row) {
      row.classList.remove("hidden");
      // A remove may have checked "remove brand image"; re-adding keeps the image the block shows.
      const removeImage = this.removeImageCheckbox(row);
      if (removeImage) removeImage.checked = false;
      // Focus the newly revealed block's first field so the admin can type right away.
      row.querySelector("input[type='text'], textarea")?.focus();
    }
    this.optionFor(brandId)?.classList.add("hidden");
    this.updatePicker();
  }

  remove(event) {
    event.preventDefault();
    const brandId = event.params.brand;
    const row = this.rowFor(brandId);
    if (row) {
      // Clear the text (and any just-picked image file) so the save removes the override, and check
      // "remove brand image" (when present) so an existing image goes too — a fully-cleared override
      // row is dropped by Nexus. Clearing the file input matters: a picked file otherwise wins over the
      // remove checkbox and would upload instead of remove.
      row.querySelectorAll("input[type='text'], input[type='file'], textarea").forEach((field) => {
        field.value = "";
      });
      const removeImage = this.removeImageCheckbox(row);
      if (removeImage) removeImage.checked = true;
      // Reset each image field's preview so a re-added row shows the server image (what the save
      // keeps once the checkbox is unchecked again), not a cleared file's blob.
      row.querySelectorAll("[data-controller~='admin--pos-image-field']").forEach((el) => {
        this.application.getControllerForElementAndIdentifier(el, "admin--pos-image-field")?.reset();
      });
      row.classList.add("hidden");
      // Removal is a real edit, but assigning value/checked fires no event, so the form's
      // unsaved-changes guard wouldn't notice. Emit a bubbling change so it marks the form dirty.
      row.dispatchEvent(new Event("change", { bubbles: true }));
    }
    this.optionFor(brandId)?.classList.remove("hidden");
    this.updatePicker();
  }

  updatePicker() {
    if (!this.hasPickerTarget) return;
    const anyVisible = this.optionTargets.some((option) => !option.classList.contains("hidden"));
    this.pickerTarget.classList.toggle("hidden", !anyVisible);
  }

  rowFor(brandId) {
    return this.rowTargets.find((row) => row.dataset.brandId === brandId);
  }

  removeImageCheckbox(row) {
    return row.querySelector("input[type='checkbox'][name$='[remove_image]']");
  }

  optionFor(brandId) {
    return this.optionTargets.find((option) => option.dataset.brandId === brandId);
  }
}
